import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProposalFormModal — Shared proposal creation form.
 *
 * Extracted from MessagingThread to be reusable by ConversationThread.
 * Calls the EXISTING proposal creation endpoint: POST /api/inquiries/{id}/proposal
 * All validation (Stripe minimums, vendor auth, duplicate checks) is handled server-side.
 *
 * Props:
 *  - inquiryId: string
 *  - onClose: () => void
 *  - onProposalSent: () => void (called after successful creation)
 *  - existingProposal: object|null (for UI labeling — rejected/revised vs new)
 */
const ProposalFormModal = ({ inquiryId, onClose, onProposalSent, existingProposal }) => {
  const [formData, setFormData] = useState({
    agreed_price: '',
    scope_description: '',
    estimated_timeline: '',
    vendor_terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const STRIPE_MIN_CHARGE = 0.50;
  const DEPOSIT_PERCENTAGE = 15;
  const MIN_TOTAL_FOR_DEPOSIT = Math.ceil((STRIPE_MIN_CHARGE / (DEPOSIT_PERCENTAGE / 100)) * 100) / 100;

  const calculatedDeposit = formData.agreed_price
    ? (parseFloat(formData.agreed_price) * (DEPOSIT_PERCENTAGE / 100)).toFixed(2)
    : '0.00';
  const isDepositTooLow = parseFloat(calculatedDeposit) < STRIPE_MIN_CHARGE && parseFloat(formData.agreed_price) > 0;

  const isRevised = existingProposal?.status === 'rejected' || existingProposal?.status === 'expired';
  const formTitle = isRevised ? 'Send Revised Proposal' : 'Send Proposal';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.agreed_price || parseFloat(formData.agreed_price) <= 0) {
      setError('Please enter a valid price');
      return;
    }

    if (isDepositTooLow) {
      setError(`Price too low for online payment. With a ${DEPOSIT_PERCENTAGE}% deposit, the minimum total is $${MIN_TOTAL_FOR_DEPOSIT.toFixed(2)} (deposit must be at least $${STRIPE_MIN_CHARGE.toFixed(2)}).`);
      return;
    }

    if (!formData.scope_description.trim()) {
      setError('Please describe the scope of work');
      return;
    }

    if (!formData.vendor_terms_accepted) {
      setError('You must accept the platform terms to send a proposal');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/inquiries/${inquiryId}/proposal`,
        {
          agreed_price: parseFloat(formData.agreed_price),
          scope_description: formData.scope_description,
          estimated_timeline: formData.estimated_timeline || null,
          vendor_terms_accepted: formData.vendor_terms_accepted,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onProposalSent();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send proposal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[80] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-5 pt-5 pb-3 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold text-slate-900">{formTitle}</h3>
          <button
            data-testid="proposal-form-close"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* 48-hour info */}
        <div className="mx-5 mt-4 mb-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5">
          <p className="text-xs text-blue-700">
            <strong>48-hour reservation.</strong> Once sent, the customer has 48 hours to review and secure your services with a deposit.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Total Price *</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-medium">$</span>
              <input
                data-testid="proposal-price-input"
                type="number"
                step="0.01"
                min="0"
                value={formData.agreed_price}
                onChange={e => setFormData({ ...formData, agreed_price: e.target.value })}
                className={`w-full pl-8 border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all ${
                  isDepositTooLow ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
                placeholder="0.00"
                required
              />
            </div>
            {formData.agreed_price && parseFloat(formData.agreed_price) > 0 && (
              <div className={`mt-1.5 text-xs ${isDepositTooLow ? 'text-red-600' : 'text-slate-500'}`}>
                {isDepositTooLow ? (
                  <span>Deposit ${calculatedDeposit} is below Stripe's $0.50 min. Minimum total: <strong>${MIN_TOTAL_FOR_DEPOSIT.toFixed(2)}</strong></span>
                ) : (
                  <span>Customer deposit ({DEPOSIT_PERCENTAGE}%): <strong>${calculatedDeposit}</strong></span>
                )}
              </div>
            )}
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Scope of Work *</label>
            <textarea
              data-testid="proposal-scope-input"
              value={formData.scope_description}
              onChange={e => setFormData({ ...formData, scope_description: e.target.value })}
              rows={3}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all resize-none"
              placeholder="Describe what's included in this proposal..."
              required
            />
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Timeline <span className="text-slate-400 font-normal">(optional)</span></label>
            <input
              data-testid="proposal-timeline-input"
              type="text"
              value={formData.estimated_timeline}
              onChange={e => setFormData({ ...formData, estimated_timeline: e.target.value })}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all"
              placeholder="e.g., 2 weeks, 1 month"
            />
          </div>

          {/* Terms */}
          <label className="flex items-start gap-2.5 bg-slate-50 border border-slate-100 rounded-xl p-3.5 cursor-pointer">
            <input
              data-testid="proposal-terms-checkbox"
              type="checkbox"
              checked={formData.vendor_terms_accepted}
              onChange={e => setFormData({ ...formData, vendor_terms_accepted: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-xs text-slate-600 leading-relaxed">
              I confirm my availability and agree to CeleyApp's{' '}
              <a href="/faqs" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">
                Service Provider Terms
              </a>. I commit to providing the described services if the customer accepts and pays.
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3.5 py-2.5 rounded-xl text-xs">{error}</div>
          )}

          {/* Actions */}
          <div className="flex gap-2.5 pt-1">
            <Button
              data-testid="proposal-submit-btn"
              type="submit"
              disabled={loading || !formData.vendor_terms_accepted}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white h-10 text-sm font-semibold rounded-xl"
            >
              {loading ? 'Sending...' : formTitle}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 h-10 text-sm rounded-xl border-slate-200"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProposalFormModal;
